import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getRootFolder, getFolderById, type FolderItem, type FolderContentItem } from '@/api/folder';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectCurrentGroupId } from '@/store/selectors';
import { setItemsForParent, selectItemsForParent, selectParentMeta, selectCacheAge, CACHE_MAX_AGE_MS } from '@/store/itemsSlice';
import { useSession } from '@/hooks/useSession';
import { useViewMode } from '@/hooks/useViewMode';
import { FolderHeader } from './FolderHeader';
import { FolderItemList } from './FolderItemList';
import { filterByType, type ShowType } from './utils';
import { Button } from '@/components/ui/Button';
import type { NormalizedItem } from '@/components/itemList/types';
import ItemListLoading from '../../components/itemListLoading/ItemListLoading';
import ItemNotFound from '../../components/itemNotFound/ItemNotFound';
import UserHasNoGroups from '../../components/userHasNoGroups/UserHasNoGroups';

type LoadStatus = 'initial' | 'loading' | 'success' | 'error';

const DEFAULT_SHOW_TYPES: ShowType[] = [
  { label: 'video', filterStatus: false },
  { label: 'audio', filterStatus: false },
  { label: 'image', filterStatus: false },
  { label: 'doc', filterStatus: false },
  { label: 'folder', filterStatus: false },
  { label: 'project', filterStatus: false },
];

export default function FolderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workspaceGroupId = useAppSelector(selectCurrentGroupId);
  const currentUserInfo = useAppSelector((state) => state.user.oldUserDetails) as
    | { role: { S: string }; [key: string]: unknown }
    | null;

  const dispatch = useAppDispatch();
  const { groupsList, updateIsQuerying, updateCurrentSession, updateCurrentItem } = useSession();

  const history = { push: (path: string) => navigate(path) };

  const [status, setStatus] = useState<LoadStatus>('initial');
  const [items, setItems] = useState<FolderContentItem[]>([]);
  const [currentItem, setCurrentItem] = useState<FolderItem | { id: string }>({ id: 'root' });

  // Sort, filter & view mode state
  const [sortCriteriaIndex, setSortCriteriaIndex] = useState(0);
  const [sortAscending, setSortAscending] = useState(true);
  const [showTypes, setShowTypes] = useState<ShowType[]>(DEFAULT_SHOW_TYPES);
  const [viewMode, setViewMode] = useViewMode(id === 'root' ? 'home' : 'folder');

  const isRoot = id === 'root';
  const hasGroups = groupsList && groupsList.length > 0;
  const isViewer = currentUserInfo?.role?.S === 'viewer';

  // ── Cache read ────────────────────────────────────────────────────

  const parentId = id || 'root';
  const cachedItems = useAppSelector(selectItemsForParent(parentId));
  const cachedMeta = useAppSelector(selectParentMeta(parentId));
  const cacheAge = useAppSelector(selectCacheAge(parentId));
  const isCacheStale = cacheAge !== undefined && cacheAge > CACHE_MAX_AGE_MS;
  const prevIdRef = useRef(parentId);

  // ── Fetch from API ────────────────────────────────────────────────

  const loadFolder = useCallback(async (background = false) => {
    if (!background) {
      setStatus('loading');
    }
    updateIsQuerying(true);
    try {
      const result = isRoot ? await getRootFolder() : await getFolderById(id!);

      // Redirect new users with empty workspace to welcome
      if (isRoot && result.data.length === 0 && currentUserInfo && !isViewer) {
        history.push('/in/welcome');
      }

      const folder = result.folder ?? { id: 'root' };
      setItems(result.data);
      dispatch(setItemsForParent({
        parentId,
        items: result.data as NormalizedItem[],
        parentMeta: folder as Record<string, unknown>,
      }));
      setCurrentItem(folder);
      updateCurrentItem(folder);
      setStatus('success');
    } catch {
      if (!background) {
        await updateCurrentSession();
        setStatus('error');
      }
    } finally {
      updateIsQuerying(false);
    }
  }, [id, parentId, isRoot, currentUserInfo, isViewer, updateIsQuerying, updateCurrentSession, dispatch, updateCurrentItem]);

  // Load on mount and when folder ID or workspace changes
  useEffect(() => {
    if (!hasGroups) return;

    // Reset filters when navigating to a different folder
    const folderChanged = prevIdRef.current !== parentId;
    prevIdRef.current = parentId;
    if (folderChanged) {
      setShowTypes(DEFAULT_SHOW_TYPES);
      setSortCriteriaIndex(0);
      setSortAscending(true);
    }

    // Cache hit: show items instantly
    if (cachedItems && cachedItems.length > 0) {
      setItems(cachedItems as FolderContentItem[]);
      if (cachedMeta) {
        setCurrentItem(cachedMeta as FolderItem);
        updateCurrentItem(cachedMeta);
      }
      setStatus('success');

      // Stale cache: also refresh in background
      if (isCacheStale) {
        loadFolder(true);
      }
      return;
    }

    // Cache miss (or empty cache for first visit): full fetch with loading state
    loadFolder();
  }, [parentId, workspaceGroupId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasGroups) {
    if (groupsList && groupsList.length === 0) {
      return <UserHasNoGroups />;
    }
    return null;
  }

  const filteredItems = filterByType(items, showTypes);

  return (
    <div className="mt-4">
      <FolderHeader
        sortCriteriaIndex={sortCriteriaIndex}
        sortAscending={sortAscending}
        showTypes={showTypes}
        onSortCriteriaChange={() => setSortCriteriaIndex((i) => (i >= 2 ? 0 : i + 1))}
        onSortDirectionToggle={() => setSortAscending((v) => !v)}
        onShowTypesChange={setShowTypes}
        showTypeFilter={!isRoot}
        disabled={status !== 'success'}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {status === 'loading' && <ItemListLoading amount={12} mode={viewMode} />}

      {status === 'success' && (
        <>
          <FolderItemList
            items={filteredItems as NormalizedItem[]}
            sortCriteriaIndex={sortCriteriaIndex}
            sortAscending={sortAscending}
            onRefresh={loadFolder}
            currentFolderId={id!}
            userRole={currentUserInfo?.role?.S}
            viewMode={viewMode}
          />
          {items.length === 0 && isViewer && (
            <div className="mx-auto w-60 pt-12 text-center">
              <h5 className="text-2xl">No viewable content</h5>
            </div>
          )}
        </>
      )}

      {status === 'error' && (
        isRoot ? (
          <div className="flex h-[500px] w-full flex-col items-center justify-center">
            <h5 className="mb-4">Something went wrong</h5>
            <Button onClick={loadFolder}>Try again</Button>
          </div>
        ) : (
          <ItemNotFound history={history} />
        )
      )}
    </div>
  );
}
